/**
 * Sell-price arithmetic shared by the flight and hotel ladders.
 *
 * Both `apps/api/src/modules/tbo-flight/flight-pricing.ts` and its hotel
 * counterpart are deliberately separate calculators — the two ladders differ in
 * almost every term (per-pax vs per-room-night fees, ancillaries, what the
 * invoice echoes back) and folding them together would make the next divergence
 * a refactor rather than an edit. What they must NOT differ on is the two things
 * below: how a rate is rounded, and what the customer is told they are paying
 * us. Those live here so one change reaches both.
 *
 * All money is BigInt minor units (paise), per the platform-wide convention.
 */

/**
 * Multiply by a basis-point rate, rounding half-up.
 *
 * Rounding matters: `5000n * 500n / 10000n` truncates in BigInt division, so a
 * fare ending in an odd paisa would quietly under-collect tax on every booking.
 * Half-up keeps the total reconcilable against the invoice.
 *
 * Shared rather than copied into each calculator: a rounding policy that holds
 * for flights and not for hotels is not a policy, and the two implementations
 * were byte-identical anyway.
 */
export function applyBps(amount: bigint, bps: number): bigint {
  if (bps <= 0 || amount === 0n) return 0n;
  const scaled = amount * BigInt(Math.round(bps));
  const q = scaled / 10_000n;
  const r = scaled % 10_000n;
  return r * 2n >= 10_000n ? q + 1n : q;
}

/**
 * Nearest whole unit (100 minor units), half-up: 234.51 -> 235, 234.49 -> 234,
 * 234.50 -> 235. The one rounding rule for a price a person sees or pays -
 * both calculators' totals and the website's `money()` go through it.
 */
export function roundToWholeUnit(minor: bigint): bigint {
  const r = ((minor % 100n) + 100n) % 100n;
  const down = minor - r;
  return r >= 50n ? down + 100n : down;
}

/**
 * The payable total, rounded to the whole unit, and the round-off that got it
 * there (signed; within one unit).
 *
 * Never rounds below `floorPaise` - the supplier net. With no margin on a fare,
 * rounding ₹7,500.49 down to ₹7,500 would charge less than we owe the supplier,
 * so that case rounds up instead.
 */
export function roundOffTotal(
  exactPaise: bigint,
  floorPaise: bigint,
): { totalPaise: bigint; roundOffPaise: bigint } {
  let totalPaise = roundToWholeUnit(exactPaise);
  if (totalPaise < floorPaise) totalPaise += 100n;
  return { totalPaise, roundOffPaise: totalPaise - exactPaise };
}

/**
 * A stored booking's round-off: the total less every component that is stored.
 *
 * No column holds it - the components and the total are persisted, so it is
 * derived, and a booking made before rounding existed derives exactly 0. A gap
 * of a whole unit or more is not a round-off (it would be some older mismatch),
 * so it is reported as '0' rather than printed under that label.
 */
export function derivedRoundOffPaise(
  totalPaise: string | bigint,
  parts: ReadonlyArray<string | bigint>,
): string {
  try {
    const diff = BigInt(totalPaise) - parts.reduce<bigint>((sum, p) => sum + BigInt(p), 0n);
    return diff > -100n && diff < 100n ? diff.toString() : '0';
  } catch {
    return '0';
  }
}

type Stored = bigint | string;

/**
 * A stored booking's round-off, listing every stored component once.
 *
 * The single caller-facing form of `derivedRoundOffPaise`: the checkout
 * sessions and the admin screens all derive it through here, so a component
 * column added later is added in one place rather than in four field lists.
 */
export function bookingRoundOffPaise(b: {
  sellPaise: Stored;
  basePaise: Stored;
  taxPaise: Stored;
  ssrPaise: Stored;
  markupPaise: Stored;
  convenienceFeePaise: Stored;
  gstPaise: Stored;
  tcsPaise: Stored;
}): string {
  return derivedRoundOffPaise(b.sellPaise, [
    b.basePaise,
    b.taxPaise,
    b.ssrPaise,
    b.markupPaise,
    b.convenienceFeePaise,
    b.gstPaise,
    b.tcsPaise,
  ]);
}

/**
 * A booking's net profit, in paise, EXCLUDING GST: our markup, plus the agency
 * discount TBO gives us (its published fare over what it actually billed our
 * wallet), plus any margin on add-ons (what the guest paid for seats over what
 * the supplier charged for them), LESS the payment-gateway fee it cost us to
 * collect the money.
 *
 * Each supplier / gateway term counts only when its figure is known - an
 * un-captured one is 0, so the number is a floor that firms up once the
 * settlement and the gateway fee are recorded. Signed: a thin margin can go
 * negative once the gateway takes its cut.
 *
 * The ONE definition of "profit" - the ticketing list, the booking detail and
 * the summary total all derive it here so they cannot drift apart. (The
 * summary total sums the same terms in SQL; keep the two in step.)
 */
export function bookingNetProfitPaise(b: {
  markupPaise: Stored;
  ssrPaise: Stored;
  supplierNetMinor: Stored;
  actualSupplierNetMinor: Stored | null;
  supplierPublishedMinor: Stored | null;
  gatewayFeeMinor: Stored | null;
}): string {
  try {
    const markup = BigInt(b.markupPaise);
    const quote = BigInt(b.supplierNetMinor);
    const ssr = BigInt(b.ssrPaise);
    const published = b.supplierPublishedMinor == null ? null : BigInt(b.supplierPublishedMinor);
    const actual = b.actualSupplierNetMinor == null ? null : BigInt(b.actualSupplierNetMinor);
    const fee = b.gatewayFeeMinor == null ? 0n : BigInt(b.gatewayFeeMinor);
    // Published over actual = the agency discount on the fare; seats cancel here.
    const agentDiscount = published != null && actual != null ? published - actual : 0n;
    // What the guest paid for add-ons over what the supplier charged (published
    // minus quote = the supplier's add-on cost, since the quote excludes them).
    const addonMargin = published != null ? ssr - (published - quote) : 0n;
    return (markup + agentDiscount + addonMargin - fee).toString();
  } catch {
    return '0';
  }
}

/** What the rounding line is called on admin screens - customers never see one. */
export const ROUND_OFF_LABEL = 'Round off';

/** What the combined fee line is called wherever a customer sees it. */
export const CONVENIENCE_FEE_LABEL = 'Convenience Fees';

/** Sub-label for the surfaces that annotate each row (payment, voucher). */
export const CONVENIENCE_FEE_NOTE = 'Utazzo platform, incl. GST';

/**
 * What a price-discovery page must tell the traveller.
 *
 * Load-bearing, not decoration: search results, the fare calendar and the room
 * lists all quote the supplier's own fare, so the total RISES at checkout when
 * the "Convenience Fees" line is added. This sentence is the only thing on those
 * pages that says so, which is why it lives beside the fee it describes rather
 * than as a literal in each component — four copies of a legally meaningful
 * sentence drift, and the shortest copy always loses a clause.
 */
export const FLIGHT_FEE_DISCLOSURE =
  'Fares include airline taxes. Our convenience fee is added at the next step, where you will see the final amount before you pay.';

/**
 * The hotel counterpart, and no longer a warning.
 *
 * Hotel search now quotes the all-in price - the property's rate plus our
 * convenience fee - so there is nothing left to disclose as "added later". The
 * sentence stayed rather than being deleted because a price-discovery page still
 * has to say what its number contains; it just says "everything" now instead of
 * "everything except our fee".
 */
export const HOTEL_FEE_DISCLOSURE =
  'Rates include hotel taxes and our convenience fee (incl. GST). This is the amount you pay - nothing is added at checkout.';

/**
 * The single "Convenience Fees" figure a traveller is shown.
 *
 * Our markup, the per-unit fee and the GST on both are three rows in the ledger
 * and one line on the screen. The flight and hotel breakdowns keep them apart
 * because the GST has to be reported on its own and an invoice raised years
 * later must be able to rebuild the components — but someone reading a fare
 * summary is owed one number for what the agent charges, not a tax lesson.
 *
 * Every customer-facing surface calls this — both funnels' fare summaries, both
 * payment steps and the hotel voucher — so none of them can drift into showing a
 * different sum of the same three fields, and adding a fourth component changes
 * one function rather than six screens. Ops keeps the split: the admin booking
 * detail and the refund quotes itemise all three, because a refund decision is
 * made against the ledger, not against the customer's summary.
 *
 * `markupPaise` is optional because the website never receives it: the proxy in
 * `app/api/secure` folds our margin into `convenienceFeePaise` and drops the
 * field before the browser sees a session (see `sanitise.ts`), so the two
 * components arrive pre-summed. Server-side callers and the admin app, which
 * hold the full breakdown, pass all three and get the same figure. Absent is
 * therefore "already counted", not "zero markup" — which is why it defaults to
 * 0 rather than being required.
 *
 * Throws on a malformed component rather than treating it as zero. These come
 * straight off a server-computed breakdown; a value that will not parse means
 * the total is wrong, and quietly under-stating what we charged is the one
 * outcome worse than a visible failure.
 */
export function convenienceFeeTotalPaise(fare: {
  markupPaise?: string;
  convenienceFeePaise: string;
  gstPaise: string;
}): string {
  return (
    BigInt(fare.markupPaise ?? '0') +
    BigInt(fare.convenienceFeePaise) +
    BigInt(fare.gstPaise)
  ).toString();
}

/**
 * The Convenience Fees figure a customer summary SHOWS: the total less every
 * other displayed row, each rounded to the whole rupee the way `money()` prints
 * it.
 *
 * Customers see no "Round off" line, and each row is rounded on its own, so
 * without this the rows could miss the (whole-rupee) total by a rupee or two -
 * base 100.40 + tax 100.40 + fee 100.40 prints 100 + 100 + 100 under a ₹301
 * total. The difference lands in OUR line, never in the supplier's base or tax.
 * Display only: the ledger components (`convenienceFeeTotalPaise`) and the
 * charged total are unchanged.
 */
export function convenienceFeeDisplayPaise(fare: {
  totalPaise: string;
  basePaise: string;
  taxPaise: string;
  ssrPaise?: string;
}): string {
  const shown = [fare.basePaise, fare.taxPaise, fare.ssrPaise ?? '0'].reduce(
    (sum, part) => sum + roundToWholeUnit(BigInt(part)),
    0n,
  );
  return (BigInt(fare.totalPaise) - shown).toString();
}
